import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import { AppUser, Listing } from '../../../types';
import { MailStatus, useSendListingEmail } from '../../../hooks/useListingEmails';
import { emailDraft, ListingEmailKind } from '../../../utils/emailDrafts';
import { TextArea, TextField } from '../listing/formFields';

const BUTTON = 'flex-1 inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 rounded-[10px] text-sm font-medium transition-opacity disabled:opacity-40';
const SUBJECT_MAX = 200;
const MESSAGE_MAX = 5000;

/**
 * Write an email to a listing's owners (docs/email-plan.md). Starts with a
 * ready-made text in Hebrew and English; nothing is sent until Send is pressed.
 */
export default function EmailComposer({
  listing,
  users,
  kind,
  reason = '',
  onDone,
}: {
  listing: Listing;
  users: AppUser[];
  kind: ListingEmailKind;
  reason?: string;
  /** Called after Skip / Close; without it there is no Skip button (the panel stays). */
  onDone?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [draft] = useState(() => emailDraft(kind, listing, reason, i18n.getFixedT('he'), i18n.getFixedT('en')));
  const [subject, setSubject] = useState(draft.subject);
  const [message, setMessage] = useState(draft.message);
  const [result, setResult] = useState<{ status: MailStatus | 'error'; to: string[] } | null>(null);
  const { sending, send } = useSendListingEmail();

  const recipients = (listing.ownerUids ?? [])
    .map((uid) => users.find((u) => u.uid === uid)?.email)
    .filter((e): e is string => !!e);

  const handleSend = async () => {
    setResult(null);
    try {
      setResult(await send({ listingId: listing.id, kind, subject: subject.trim(), message: message.trim() }));
    } catch {
      setResult({ status: 'error', to: [] });
    }
  };

  const done = result && result.status !== 'error';

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink">
        <span className="text-ash">{t('directory.super.email.to')}: </span>
        {recipients.length ? (
          <bdi dir="ltr" className="break-all">{recipients.join(', ')}</bdi>
        ) : (
          <span className="text-danger-600">{t('directory.super.email.noOwner')}</span>
        )}
      </p>

      {!done && (
        <>
          <TextField label={t('directory.super.email.subject')} value={subject} onChange={setSubject} maxLength={SUBJECT_MAX} />
          <TextArea
            label={t('directory.super.email.message')}
            hint={t('directory.super.email.messageHint')}
            value={message}
            onChange={setMessage}
            rows={10}
            maxLength={MESSAGE_MAX}
            mixedLanguages
          />
        </>
      )}

      {result && (
        <p role="status" className={`text-sm ${result.status === 'error' ? 'text-danger-600' : 'text-ink'}`}>
          {result.status === 'error'
            ? t('directory.super.email.failed')
            : result.status === 'notSent'
            ? t('directory.super.email.notSent')
            : t('directory.super.email.sent', { to: result.to.join(', ') })}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        {!done && (
          <button
            type="button"
            disabled={sending || !recipients.length || !subject.trim() || !message.trim()}
            onClick={handleSend}
            className={`${BUTTON} bg-ink text-paper`}
          >
            <Send className="w-4 h-4 rtl:-scale-x-100" aria-hidden />
            {sending ? t('directory.super.email.sending') : t('directory.super.email.send')}
          </button>
        )}
        {onDone && (
          <button type="button" disabled={sending} onClick={onDone} className={`${BUTTON} border border-ink text-ink`}>
            {done ? t('directory.super.email.close') : t('directory.super.email.skip')}
          </button>
        )}
      </div>
    </div>
  );
}
